export type TipoCuenta = 'INGRESO' | 'GASTOS';
export type BatchStatus = 'Cargado' | 'Procesado';
export type StagingStatus = 'No revisado' | 'Revisado' | 'Procesado';
/** 'ACCOUNT' = fila de cuenta normal. 'SECTION_TOTAL' = fila "TOTAL INGRESOS:"/"TOTAL EGRESOS:" del Excel. 'UTILIDAD' = fila "UTILIDAD:" del Excel. */
export type SnapshotRowType = 'ACCOUNT' | 'SECTION_TOTAL' | 'UTILIDAD';
/** El archivo mensual ya solo trae una hoja, así que "Por revisar" ahora mira datos dudosos dentro de ella misma. */
export type ValidationMotivo = 'CUENTA_SIN_REPORTE' | 'VALOR_MUY_PEQUENO' | 'VALOR_NEGATIVO' | 'TOTAL_NO_CUADRA' | 'CLIENTE_SIN_TIPO_SERVICIO';

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ImportBatch {
  id: string;
  sourceMonth: string;
  sourceYear: number;
  fileName: string;
  status: BatchStatus;
  totalIngresos: number;
  totalGastos: number;
  utilidad: number;
  validationErrorCount: number;
  createdDate: string;
  createdBy: string | null;
}

export interface SnapshotDetail {
  id: string;
  accountCode: string;
  accountDescription: string;
  tipoCuenta: TipoCuenta | null;
  rowType: SnapshotRowType;
  clientColumn: string;
  isTotalColumn: boolean;
  value: number;
}

export interface SnapshotRowInput {
  accountCode: string;
  accountDescription: string;
  tipoCuenta: TipoCuenta | null;
  rowType: SnapshotRowType;
  values: Record<string, number>;
}

export interface CreateImportBatchInput {
  sourceMonth: string;
  sourceYear: number;
  fileName: string;
  sheetNameUsed: string;
  snapshotRows: SnapshotRowInput[];
  smallValueThreshold?: number;
}

export interface CreateImportBatchResult {
  batch: ImportBatch;
  validationErrors: ValidationError[];
}

export interface ValidationError {
  id: string;
  cuentaCodigo: string;
  cuentaDescripcion: string | null;
  cliente: string | null;
  valor: number | null;
  valorEsperado: number | null;
  diferencia: number | null;
  motivo: ValidationMotivo;
}

export interface AccountDetailStagingRow {
  id: string;
  importBatchId: string;
  mes: string;
  anio: number;
  cliente: string;
  tipoServicio: string;
  cuentaCodigo: string;
  cuentaDescripcion: string;
  tipoCuenta: TipoCuenta;
  valor: number;
  status: StagingStatus;
}
export type UpdateStagingRowInput = Partial<Pick<AccountDetailStagingRow, 'cliente' | 'tipoServicio' | 'cuentaCodigo' | 'cuentaDescripcion' | 'tipoCuenta' | 'valor' | 'status'>>;
export interface ListStagingFilters {
  importBatchId?: string;
  mes?: string;
  anio?: number;
  status?: string;
}

export interface AccountDetail {
  id: string;
  mes: string;
  anio: number;
  cliente: string;
  tipoServicio: string;
  cuentaCodigo: string;
  cuentaDescripcion: string;
  tipoCuenta: TipoCuenta;
  valor: number;
}
export type UpdateAccountDetailInput = Partial<Pick<AccountDetail, 'cliente' | 'tipoServicio' | 'cuentaCodigo' | 'cuentaDescripcion' | 'tipoCuenta' | 'valor'>>;
export interface ListAccountDetailFilters {
  mes?: string;
  anio?: number;
  cliente?: string;
  tipoServicio?: string;
  includeEmpty?: boolean;
  page?: number;
  pageSize?: number;
}

export interface SummaryFilters {
  anio: number;
  tiposServicio?: string[];
  clientes?: string[];
}
export interface SummaryRow {
  tipoCuenta: TipoCuenta;
  valuesByMonth: Record<string, number>;
  total: number;
}

export interface SummaryDetailFilters {
  anio: number;
  tipoCuenta: TipoCuenta;
  tiposServicio?: string[];
  clientes?: string[];
}
export interface SummaryDetailRow {
  cuentaCodigo: string;
  cuentaDescripcion: string;
  valuesByMonth: Record<string, number>;
  total: number;
}

/** "Comparar Clientes" - entre "Datos Tabla" y "Tabla Resumen": rankea clientes en vez de colapsarlos en un solo total. */
export interface ClientComparisonFilters {
  anio: number;
  /** Selección múltiple (checklist, igual que "Tabla Resumen"). Vacío/undefined = todos. */
  tiposServicio?: string[];
  clientes?: string[];
  /** Selección múltiple de meses a comparar lado a lado. Vacío/undefined = todos los meses cargados de ese año. */
  meses?: string[];
}
export interface ClientMonthValue {
  ingreso: number;
  gastos: number;
  utilidad: number;
}
export interface ClientComparisonRow {
  cliente: string;
  valuesByMonth: Record<string, ClientMonthValue>;
  totalIngreso: number;
  totalGastos: number;
  totalUtilidad: number;
}

export interface AvailableMonth {
  mes: string;
  anio: number;
  batchStatus: BatchStatus;
  pendingStagingCount: number;
}

// ---------------------------------------------------------------------
// Parametros
// ---------------------------------------------------------------------
export interface ClientMapping {
  id: string;
  clienteDatosTabla: string;
  clienteResultadosMes: string;
  nombreCorto: string;
  tipoServicio: string | null;
  isActive: boolean;
}
export type CreateClientMappingInput = Omit<ClientMapping, 'id'>;
export type UpdateClientMappingInput = Partial<CreateClientMappingInput>;

export interface AccountCatalogItem {
  id: string;
  cuentaCodigo: string;
  cuentaDescripcion: string;
  tipoCuenta: TipoCuenta;
  isActive: boolean;
}
export type CreateAccountInput = Omit<AccountCatalogItem, 'id'>;
export type UpdateAccountInput = Partial<CreateAccountInput>;

export interface ServiceType {
  id: string;
  nombre: string;
  isActive: boolean;
}
export type CreateServiceTypeInput = Omit<ServiceType, 'id'>;
export type UpdateServiceTypeInput = Partial<CreateServiceTypeInput>;

export const MONTH_NAMES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
